export interface KeyboardHandler {
  (event: KeyboardEvent): void;
}

export interface ContextHandlers {
  [key: string]: KeyboardHandler;
}

export interface ContextData {
  handlers: ContextHandlers;
  registrationOrder: number;
  focus?: string;
}

export interface KeyboardControllerConfig {
  handlers?: ContextHandlers;
  focus?: string;
}

export interface KeyboardControllerInstance {
  registerHandlers: (context: string, config: KeyboardControllerConfig) => void;
  unregister: (context: string) => void;
  focus: (context?: string) => void;
  clear: () => void;
  getFocus: (context: string) => (selector: string) => void;
  (context: string, config: KeyboardControllerConfig): void;
}

let $keyboard: KeyboardControllerInstance;

const createKeyboardController = () => {
  const contexts = new Map<string, ContextData>();
  const activeContexts = new Set<string>();
  const focusAttempts = new Map<string, number>();
  let registrationCounter = 0;
  const MAX_FOCUS_ATTEMPTS = 10;
  const FOCUS_RETRY_DELAY = 300;

  const attemptFocus = (selector: string): boolean => {
    const element = document.querySelector(selector) as HTMLElement;
    if (!element) return false;

    document.activeElement instanceof HTMLElement && document.activeElement.blur();
    element.focus();
    return document.activeElement === element;
  };

  const focusElement = (context: string, selector: string): void => {
    if (!activeContexts.has(context)) return;

    const attempts = focusAttempts.get(selector) || 0;
    if (attempts >= MAX_FOCUS_ATTEMPTS) {
      focusAttempts.delete(selector);
      console.warn(`Failed to focus element "${selector}" after ${MAX_FOCUS_ATTEMPTS} attempts`);
      return;
    }

    if (attemptFocus(selector)) {
      focusAttempts.delete(selector);
      return;
    }

    focusAttempts.set(selector, attempts + 1);
    setTimeout(() => activeContexts.has(context) ? focusElement(context, selector) : focusAttempts.delete(selector), FOCUS_RETRY_DELAY);
  };

  const isContextMapItem = (context: any): context is { contextId: string, data: ContextData } => context.data !== undefined;

  const handleKeyPress = (event: KeyboardEvent) => {
    if (activeContexts.size === 0) return;

    const orderedContexts = Array.from(activeContexts)
      .map(contextId => ({ contextId, data: contexts.get(contextId) }))
      .filter(isContextMapItem)
      .sort((a, b) => b.data.registrationOrder - a.data.registrationOrder);

    orderedContexts.forEach(({ data }) => {
      const keyHandlerKey = `${event.ctrlKey ? 'Ctrl+' : ''}${event.shiftKey ? 'Shift+' : ''}${event.altKey ? 'Alt+' : ''}${event.key}`;
      const keyCodeHandlerKey = `${event.ctrlKey ? 'Ctrl+' : ''}${event.shiftKey ? 'Shift+' : ''}${event.altKey ? 'Alt+' : ''}${event.keyCode}`;

      const handler = data.handlers[keyHandlerKey] || data.handlers[keyCodeHandlerKey];
      if (handler) {
        handler(event);
      }
    });
  };

  window.addEventListener('keydown', handleKeyPress, { capture: true });

  const keyboard = function (context: string, config: KeyboardControllerConfig) {
    keyboard.registerHandlers(context, config);
  } as KeyboardControllerInstance;

  keyboard.registerHandlers = (context: string, { handlers = {}, focus }) => {
    registrationCounter++;
    contexts.set(context, { handlers, registrationOrder: registrationCounter, focus });
    activeContexts.add(context);
    focus && focusElement(context, focus);
  };

  keyboard.unregister = (context: string) => {
    contexts.delete(context);
    activeContexts.delete(context);
  };

  keyboard.focus = (context?: string) => {
    if (context && activeContexts.has(context)) {
      const contextData = contexts.get(context);
      contextData?.focus && focusElement(context, contextData.focus);
    }
  };

  keyboard.getFocus = (context: string) => (selector: string) => focusElement(context, selector);

  keyboard.clear = () => {
    contexts.clear();
    activeContexts.clear();
    focusAttempts.clear();
    registrationCounter = 0;
  };

  return keyboard;
};

const getKeyboard = () => {
  if ($keyboard) return $keyboard;
  $keyboard = createKeyboardController();
  return $keyboard;
};

export default {
  install(app) {
    app.prototype.$keyboard = getKeyboard();
  }
};

export function useKeyboard() {
  return { keyboard: getKeyboard() };
}
